import { Button, styled, TextField, Tooltip } from '@mui/material';
import { useState } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Row } from 'components/Layout';
import { KycStatusLabel } from 'further-ui/labels';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const TooltipIcon = styled(InfoOutlined)({
  position: 'relative',
  fontSize: '14px',
  lineHeight: '14px',
  cursor: 'pointer',
  top: '3px',
});

const NotesContainer = styled(Row)({
  flexGrow: 1,
});

const NotesButton = styled(Button)({
  flex: 'none',
  flexBasis: 'auto',
});

type Props = {
  statusLabel: string | KycStatusLabel;
  approveButtonText: string;
  unapproveButtonText?: string;
  showApproveButton: boolean;
  showUnapproveButton?: boolean;
  approvalNotes?: string;
  showDownloadResultsButton: boolean;
  handleApprove?: (notes?: string) => void;
  handleUnapprove?: () => void;
  handleDownloadResults?: () => void;
  showNotesTooltip: boolean;
  handleSaveNotes?: (notes?: string) => void;
};

const KycStatus: React.FC<Props> = ({
  approveButtonText,
  handleApprove,
  handleUnapprove,
  handleDownloadResults,
  showApproveButton,
  showUnapproveButton,
  unapproveButtonText,
  showDownloadResultsButton,
  statusLabel,
  approvalNotes,
  showNotesTooltip,
  handleSaveNotes,
}) => {
  const [notes, setNotes] = useState(approvalNotes);
  const [showNotesField, setShowNotesField] = useState(showApproveButton);
  const [canEdit] = useHasPermissions([RolePermission.EditInvestorKyc]);

  return (
    <Row centered spacing="sm" fullWidth>
      <span>
        {statusLabel}{' '}
        {showNotesTooltip && (
          <Tooltip
            placement="top"
            title={`Approval note: ${
              approvalNotes?.length ? approvalNotes : 'none'
            }`}
          >
            <TooltipIcon />
          </Tooltip>
        )}
      </span>
      {showDownloadResultsButton && (
        <Button
          size="extra-small"
          variant="outlined"
          color="primary"
          onClick={handleDownloadResults}
        >
          Download results
        </Button>
      )}
      {showApproveButton && (
        <Button
          size="extra-small"
          variant="outlined"
          color="primary"
          onClick={() => {
            handleApprove?.(notes);
            setShowNotesField(false);
          }}
        >
          {approveButtonText}
        </Button>
      )}
      {showApproveButton && !showNotesField && (
        <Button
          size="extra-small"
          variant="outlined"
          color="primary"
          onClick={() => setShowNotesField(true)}
        >
          Edit note
        </Button>
      )}
      {showUnapproveButton && (
        <Button
          size="extra-small"
          variant="outlined"
          color="error"
          onClick={() => {
            handleUnapprove?.();
            setShowNotesField(true);
          }}
        >
          {unapproveButtonText}
        </Button>
      )}
      {showNotesField && (
        <NotesContainer spacing="sm">
          <TextField
            value={notes}
            variant="outlined"
            size="small"
            fullWidth
            disabled={!canEdit}
            placeholder="Enter approval note"
            onChange={(e) => setNotes(e.target.value)}
            sx={{ minHeight: 0 }}
            slotProps={{
              input: {
                sx: { minHeight: 0 },
              },
            }}
          />
          {canEdit && (
            <NotesButton
              size="extra-small"
              variant="outlined"
              color="primary"
              onClick={() => {
                handleSaveNotes?.(notes);
                setShowNotesField(false);
              }}
            >
              Save note
            </NotesButton>
          )}
        </NotesContainer>
      )}
    </Row>
  );
};

export default KycStatus;
